/** @typedef {'phaladeepika' | 'calculator' | 'quiz' | 'my-readings' | 'story' | 'reading-chart' | 'panchang' | 'chart' | 'lesson'} JyotishaTopicKind */

/** @type {Record<JyotishaTopicKind, { hi: string, en: string, shortHi: string, shortEn: string, dark: string, light: string }>} */
export const JYOTISHA_TOPIC_KIND_META = {
  phaladeepika: {
    hi: 'फलदीपिका',
    en: 'Phaladeepika',
    shortHi: 'फलदी.',
    shortEn: 'Phal.',
    dark: 'bg-amber-500/20 text-amber-100 ring-amber-400/35',
    light: 'bg-amber-100 text-amber-950 ring-amber-300/80',
  },
  calculator: {
    hi: 'कैलकुलेटर',
    en: 'Calculator',
    shortHi: 'कैल्क',
    shortEn: 'Calc',
    dark: 'bg-teal-500/20 text-teal-100 ring-teal-400/35',
    light: 'bg-teal-100 text-teal-950 ring-teal-300/80',
  },
  quiz: {
    hi: 'परीक्षा',
    en: 'Quiz',
    shortHi: 'परीक्षा',
    shortEn: 'Quiz',
    dark: 'bg-rose-500/20 text-rose-100 ring-rose-400/35',
    light: 'bg-rose-100 text-rose-950 ring-rose-300/80',
  },
  'my-readings': {
    hi: 'महत्वपूर्ण अवलोकन',
    en: 'Important Observations',
    shortHi: 'अवलोकन',
    shortEn: 'Obs.',
    dark: 'bg-violet-500/20 text-violet-100 ring-violet-400/35',
    light: 'bg-violet-100 text-violet-950 ring-violet-300/80',
  },
  story: {
    hi: 'कथा',
    en: 'Story',
    shortHi: 'कथा',
    shortEn: 'Story',
    dark: 'bg-orange-500/20 text-orange-100 ring-orange-400/35',
    light: 'bg-orange-100 text-orange-950 ring-orange-300/80',
  },
  'reading-chart': {
    hi: 'चार्ट पठन',
    en: 'Chart reading',
    shortHi: 'पठन',
    shortEn: 'Read',
    dark: 'bg-sky-500/20 text-sky-100 ring-sky-400/35',
    light: 'bg-sky-100 text-sky-950 ring-sky-300/80',
  },
  panchang: {
    hi: 'पंचांग',
    en: 'Panchang',
    shortHi: 'पंचांग',
    shortEn: 'Panch.',
    dark: 'bg-emerald-500/20 text-emerald-100 ring-emerald-400/35',
    light: 'bg-emerald-100 text-emerald-950 ring-emerald-300/80',
  },
  chart: {
    hi: 'चार्ट',
    en: 'Charts',
    shortHi: 'चार्ट',
    shortEn: 'Chart',
    dark: 'bg-indigo-500/20 text-indigo-100 ring-indigo-400/35',
    light: 'bg-indigo-100 text-indigo-950 ring-indigo-300/80',
  },
  lesson: {
    hi: 'पाठ',
    en: 'Lesson',
    shortHi: 'पाठ',
    shortEn: 'Lesson',
    dark: 'bg-slate-500/20 text-slate-100 ring-slate-400/30',
    light: 'bg-slate-100 text-slate-800 ring-slate-300/70',
  },
};

/** Query tokens that hint a topic kind (folded, lowercase). */
const KIND_QUERY_HINTS = /** @type {Record<JyotishaTopicKind, string[]>} */ ({
  phaladeepika: [
    'phaladeepika',
    'phaldeepika',
    'mantreswara',
    'mantreshwara',
    'फलदीपिका',
    'shloka',
    'श्लोक',
    'adhyay',
    'अध्याय',
  ],
  calculator: ['calc', 'calculator', 'calculate', 'कैलकुलेटर', 'गणना', 'visualizer', 'विज़ुअलाइज़र'],
  quiz: ['quiz', 'pariksha', 'परीक्षा', 'exam', 'test', 'अभ्यास'],
  'my-readings': ['myreading', 'myreadings', 'मेरेपाठ'],
  story: ['story', 'stories', 'कथा', 'कहानी', 'jyotishastories'],
  'reading-chart': ['readingchart', 'chartreading', 'चार्टपठन', 'chartread', 'house', 'houses', 'lord', 'lords', 'bhava', 'bhavesh', 'lagnesh', 'bhav', '1stlord', '8thlord'],
  panchang: ['panchang', 'पंचांग', 'muhurat', 'मुहूर्त', 'tithi', 'तिथि'],
  chart: ['charts', 'varga', 'navamsa', 'navamsha', 'दिवisional'],
  lesson: ['lesson', 'पाठ', 'prarambh', 'प्रारंभ', 'intro', 'परिचय', 'prashna', 'प्रश्न', 'horary'],
});

/**
 * @param {string} topicId
 * @returns {JyotishaTopicKind}
 */
export function getJyotishaTopicKind(topicId) {
  const id = String(topicId || '');
  if (id === 'phaladeepika' || id.startsWith('phaladeepika-')) return 'phaladeepika';
  if (id.startsWith('calc-')) return 'calculator';
  if (id.startsWith('quiz-')) return 'quiz';
  if (id.startsWith('my-readings-')) return 'my-readings';
  if (id.startsWith('jyotisha-stories-')) return 'story';
  if (id.startsWith('reading-charts-')) return 'reading-chart';
  if (id.startsWith('panchang-')) return 'panchang';
  if (id.startsWith('charts-')) return 'chart';
  return 'lesson';
}

/**
 * @param {string} foldedQuery Full query after fold()
 * @returns {JyotishaTopicKind | null}
 */
export function detectJyotishaKindFromQuery(foldedQuery) {
  const q = String(foldedQuery || '').trim();
  if (!q) return null;

  /** @type {{ kind: JyotishaTopicKind, score: number }[]} */
  const hits = [];

  for (const [kind, hints] of Object.entries(KIND_QUERY_HINTS)) {
    for (const hint of hints) {
      const fh = hint
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      if (fh.length < 2) continue;
      if (q === fh) hits.push({ kind: /** @type {JyotishaTopicKind} */ (kind), score: 120 });
      else if (q.includes(fh)) hits.push({ kind: /** @type {JyotishaTopicKind} */ (kind), score: 70 + fh.length });
    }
  }

  if (!hits.length) return null;
  hits.sort((a, b) => b.score - a.score);
  return hits[0].kind;
}

/**
 * @param {JyotishaTopicKind} kind
 * @param {boolean} showEnglish
 * @param {boolean} [short]
 */
export function getJyotishaTopicKindLabel(kind, showEnglish, short = false) {
  const meta = JYOTISHA_TOPIC_KIND_META[kind] ?? JYOTISHA_TOPIC_KIND_META.lesson;
  if (short) return showEnglish ? meta.shortEn : meta.shortHi;
  return showEnglish ? meta.en : meta.hi;
}
